import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';

export type SupportedLang = 'en' | 'ar' | 'hi' | 'ru';

// Only English is bundled with the main chunk (it's the fallback and the
// most common language). AR/HI are split into their own dynamic chunks and
// fetched on demand via loadLanguage() — saves ~440 KB from the critical path.
const loaders: Record<Exclude<SupportedLang, 'en'>, () => Promise<{ default: Record<string, unknown> }>> = {
  ar: () => import('./locales/ar.json'),
  hi: () => import('./locales/hi.json'),
  ru: () => import('./locales/ru.json'),
};

const loaded = new Set<SupportedLang>(['en']);
const inflight = new Map<SupportedLang, Promise<void>>();

export const loadLanguage = (lng: SupportedLang): Promise<void> => {
  if (loaded.has(lng)) return Promise.resolve();
  const existing = inflight.get(lng);
  if (existing) return existing;
  const loader = loaders[lng as Exclude<SupportedLang, 'en'>];
  if (!loader) return Promise.resolve();
  const p = loader().then((mod) => {
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
    loaded.add(lng);
    inflight.delete(lng);
  });
  inflight.set(lng, p);
  return p;
};

const getInitialLang = (): SupportedLang => {
  if (typeof window === 'undefined') return 'en';
  const seg = window.location.pathname.split('/')[1];
  if (seg === 'ar' || seg === 'hi' || seg === 'ru') return seg;
  return 'en';
};

const initialLang = getInitialLang();

i18n
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    lng: 'en', // start in English to render immediately; we swap below.
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar', 'hi', 'ru'],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

// Browser-only: sync <html> direction and trigger lang load.
// On the server (SSR build / prerender), `document` does not exist and the
// language is set explicitly by entry-server.tsx before renderToString.
if (!import.meta.env.SSR) {
  i18n.on('languageChanged', (lng) => {
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  });

  document.documentElement.lang = initialLang;
  document.documentElement.dir = initialLang === 'ar' ? 'rtl' : 'ltr';

  // If the URL points at a non-English locale, kick off the dynamic load and
  // switch language as soon as it lands. The page renders English in the
  // meantime — far better than a 700 KB blocking download.
  if (initialLang !== 'en') {
    loadLanguage(initialLang).then(() => i18n.changeLanguage(initialLang));
  }
}

export default i18n;
